/**
 * The canonical study-branch level icons, indexed by level (0 = Study .. 4 =
 * Subgroup). Exported at module scope rather than kept inside
 * `createCompositeKeyUtils` so consumers that have no `tags` array - QC's
 * `branchLevelDisplay` - can read the same values instead of copying them.
 * A copy is how these drifted before (DCTL-2256).
 */
export const BRANCH_LEVEL_ICONS = Object.freeze([
	"mdi-file-document-outline", // study
	"mdi-format-page-break", // study part
	"mdi-flask-outline", // study arm
	"mdi-account-group-outline", // population
	"mdi-filter-outline", // subgroup
]);

export const UNKNOWN_BRANCH_LEVEL_ICON = "mdi-circle-small";

/**
 * The tag-independent half of the composite-key vocabulary, at module scope
 * for the same reason as `BRANCH_LEVEL_ICONS`: a consumer with no `tags`
 * array (QC reads finished ammonite, not a live extraction schema) needs the
 * five level TYPES, their LABELS and the key parsers, and a copy is how they
 * drift (DCTL-2256; DCTL-2640 found QC holding a second `splitCompositeKey`
 * and a second set of level type keys). The factory below returns these same
 * functions, so its callers are unchanged.
 */
const COMPOSITE_KEY_DELIMITER = "||";

/** Level TYPE keys, in level order (the `parsedKeys` of the factory). */
export const BRANCH_LEVEL_TYPES = Object.freeze([
	"study",
	"studyPart",
	"studyArm",
	"population",
	"subgroup",
]);

/** Human level labels, in level order (the `parsedLabels` of the factory). */
export const BRANCH_LEVEL_LABELS = Object.freeze([
	"Study",
	"Study part",
	"Study arm",
	"Population",
	"Subgroup",
]);

/**
 * True for one of the five study-branch level names. The labels above are
 * also the names of the five composite-key TAGS every data tag group lists
 * and of the five branch TAG GROUPS, so this is the module-scope form of the
 * factory's `isBranchTag` / `isBranchTagGroup` for a consumer with no `tags`
 * array: QC's tag pickers, which read the `/tag-groups` roster and must never
 * offer a branch tag as a rename target.
 */
export const isBranchLevelName = (name) => BRANCH_LEVEL_LABELS.includes(name);

// Splits a composite key into a list of individual keys
export const splitCompositeKey = (key) => {
	return key.split(COMPOSITE_KEY_DELIMITER).map((part) => part.trim());
};

// Parses a composite key in an object; e.g.
// {
//   study: "study1",
//   studyPart: "part1",
//   studyArm: "arm1"
//   population: "pop1",
//   subgroup: "subgroup1"
// }
export const parseCompositeKey = (key) => {
	const splitKey = splitCompositeKey(key);
	const parsed = { key };
	BRANCH_LEVEL_TYPES.forEach((type, index) => {
		parsed[type] = splitKey[index];
	});
	return parsed;
};

export const parseCompositeKeyWithLabels = (key) => {
	const splitKey = splitCompositeKey(key);
	return splitKey.reduce((acc, part, index) => {
		if (part) {
			acc.push({
				label: BRANCH_LEVEL_LABELS[index],
				value: part,
			});
		}

		return acc;
	}, []);
};

// Gets the most specific value from a composite key; for example,
// if the key is "study1 || part1 || arm1 || pop1 || ", then the value
// would be "pop1"
export const getCompositeKeyValue = (compositeKey) => {
	const parsedKey = parseCompositeKey(compositeKey);

	if (parsedKey.subgroup) {
		return parsedKey.subgroup;
	} else if (parsedKey.population) {
		return parsedKey.population;
	} else if (parsedKey.studyArm) {
		return parsedKey.studyArm;
	} else if (parsedKey.studyPart) {
		return parsedKey.studyPart;
	} else {
		return parsedKey.study;
	}
};

// Single source of truth for rendering a study-branch composite key to a
// user. Returns the leaf branch name plus the full hierarchy path in the
// shapes the various display sites need, so callers stop re-deriving them:
//   - leaf:         most specific branch name (getCompositeKeyValue)
//   - parts:        [{ label, value }] per non-empty level
//   - inlinePath:   "Study A › Arm 1 › Subgroup X" (breadcrumb)
//   - labeledLines: ["Study: Study A", "Study arm: Arm 1", ...] (tooltip)
// Returns null when `compositeKey` isn't a parseable composite key (no
// delimiter / no non-empty parts), so callers fall back to the raw value.
export const formatBranchPath = (compositeKey) => {
	if (
		typeof compositeKey !== "string" ||
		!compositeKey.includes(COMPOSITE_KEY_DELIMITER)
	) {
		return null;
	}
	const parts = parseCompositeKeyWithLabels(compositeKey);
	if (!parts.length) return null;
	const leaf = getCompositeKeyValue(compositeKey);
	return {
		leaf: leaf || compositeKey,
		parts,
		inlinePath: parts.map((p) => p.value).join(" › "),
		labeledLines: parts.map((p) => `${p.label}: ${p.value}`),
	};
};

// Helper to get icon based on depth level
export const getIconForLevel = (level) =>
	BRANCH_LEVEL_ICONS[level] || UNKNOWN_BRANCH_LEVEL_ICON;

const createCompositeKeyUtils = (tags) => {
	const compositeKeyDelimiter = COMPOSITE_KEY_DELIMITER;

	const getCompositeKeyTags = (tags) => {
		return tags
			.filter((tag) => tag.isCompositeKeyMember)
			.sort((tagA, tagB) => tagA.compositeKeyIndex - tagB.compositeKeyIndex);
	};
	const compTags = getCompositeKeyTags(tags);

	// Tags flagged is_reference_composite_key in the schema (normally one, but
	// match defensively). Reference detection keys off this flag + the tag uuid
	// rather than a hard-coded name, so a renamed reference tag and multi-
	// reference schemas are still recognized. The literal "Reference study
	// branch" is only a last-resort fallback for tag arrays that predate the
	// flag being populated.
	const referenceBranchTagObjects = (tags || []).filter(
		(tag) => tag.isReferenceCompositeKey,
	);
	const referenceBranchTagObject = referenceBranchTagObjects[0];
	const referenceBranchTag = referenceBranchTagObject
		? referenceBranchTagObject.name
		: "Reference study branch";
	const referenceTagNameSet = new Set(
		referenceBranchTagObjects.map((tag) => tag.name).filter(Boolean),
	);
	const referenceTagUuidSet = new Set(
		referenceBranchTagObjects.map((tag) => tag.uuid).filter(Boolean),
	);

	// Older WPD saves baked "Reference study arm abbreviation" into per-pixel
	// tag refs before the tag was renamed to "Reference study branch". SDEv1
	// code (everything under src/plugins/wpd and the VMetadata* metadata
	// dialogs, including the V2 dialog) reads that raw data and must
	// recognize both names. SDEv2 (src/pages/SourceDataExtraction/*) only
	// ever loads data that has already been canonicalized by Dactylizer
	// (DactylizerServiceV2 maps the old name to the new) and should not see
	// legacy names — SDEv2 paths therefore use the strict isReferenceTag.
	const referenceBranchTagNames = [
		referenceBranchTag,
		"Reference study arm abbreviation",
	];
	const isReferenceBranchTag = (name) =>
		!!name && referenceBranchTagNames.includes(name);

	const studyTag = compTags[0].name;
	const studyPartTag = compTags[1].name;
	const studyArmTag = compTags[2].name;
	const populationTag = compTags[3].name;
	const subgroupTag = compTags[4].name;

	const branchTags = [
		studyTag,
		studyPartTag,
		studyArmTag,
		populationTag,
		subgroupTag,
	];

	const tagKeyMap = {
		[studyTag]: 0,
		[studyPartTag]: 1,
		[studyArmTag]: 2,
		[populationTag]: 3,
		[subgroupTag]: 4,
	};

	const [studyKey, studyPartKey, studyArmKey, populationKey, subgroupKey] =
		BRANCH_LEVEL_TYPES;
	const parsedKeys = BRANCH_LEVEL_TYPES;
	const parsedLabels = BRANCH_LEVEL_LABELS;

	const studyTagGroup = "Study";
	const studyPartTagGroup = "Study part";
	const studyArmTagGroup = "Study arm";
	const populationTagGroup = "Population";
	const subgroupTagGroup = "Subgroup";
	const branchTagGroups = [
		studyTagGroup,
		studyPartTagGroup,
		studyArmTagGroup,
		populationTagGroup,
		subgroupTagGroup,
	];

	// Constructs a composite key
	const generateCompositeKey = (
		studyKey,
		studyPartKey,
		studyArmKey,
		populationKey,
		subgroupKey,
	) => {
		return (
			(studyKey ? `${studyKey}` : ``) +
			(studyPartKey
				? ` ${compositeKeyDelimiter} ${studyPartKey}`
				: ` ${compositeKeyDelimiter}`) +
			(studyArmKey
				? ` ${compositeKeyDelimiter} ${studyArmKey}`
				: ` ${compositeKeyDelimiter}`) +
			(populationKey
				? ` ${compositeKeyDelimiter} ${populationKey}`
				: ` ${compositeKeyDelimiter}`) +
			(subgroupKey
				? ` ${compositeKeyDelimiter} ${subgroupKey}`
				: ` ${compositeKeyDelimiter} `)
		);
	};

	// Joins a list of keys into a composite key string
	const joinCompositeKey = (keyParts) => {
		return keyParts
			.map((part, index) => {
				if (!part) return " ";
				if (index === 0) return `${part} `;
				if (index === keyParts.length - 1) return ` ${part}`;
				return ` ${part} `;
			})
			.join(`${compositeKeyDelimiter}`);
	};

	/**
	 * Builds a composite key by adding a new branch to a parent's composite key
	 *
	 * @param {string} parentCompositeKey - Parent's composite key (or empty string for root)
	 * @param {string} newNodeType - Type of new node (e.g., "study", "studyPart", "studyArm")
	 * @param {string} newNodeName - Name/value for the new node
	 * @returns {string} New composite key
	 *
	 * Examples:
	 *   buildCompositeKey("", "study", "Study A")
	 *     → "Study A || || || ||"
	 *
	 *   buildCompositeKey("Study A || || || ||", "studyPart", "Part 1")
	 *     → "Study A || Part 1 || || ||"
	 *
	 *   buildCompositeKey("Study A || || || ||", "studyArm", "Arm A")
	 *     → "Study A || || Arm A || ||"
	 */
	const buildCompositeKey = (parentCompositeKey, newNodeType, newNodeName) => {
		// Get the index for this type (0=study, 1=study_part, 2=study_arm, etc.)
		const newNodeIndex = getIndexFromType(newNodeType);

		if (newNodeIndex === -1) {
			console.error(`Invalid node type: ${newNodeType}`);
			return parentCompositeKey || "";
		}

		// Parse parent key into array of values (or create empty array for root)
		const parentParts = parentCompositeKey
			? splitCompositeKey(parentCompositeKey)
			: Array(parsedKeys.length).fill("");

		// Set the value at the appropriate index
		parentParts[newNodeIndex] = newNodeName;

		// Clear any values after this index (child levels get reset)
		for (let i = newNodeIndex + 1; i < parentParts.length; i++) {
			parentParts[i] = "";
		}

		// Join back into composite key string
		return joinCompositeKey(parentParts);
	};

	/**
	 * Re-root a composite key's ancestor slots onto a different chain
	 * (DCTL-2532).
	 *
	 * Positional and deliberately blunt: overwrites slots `[0, levelIndex)` from
	 * `ancestorSegments` and leaves `levelIndex` and everything below it alone. A
	 * slot the new chain does not fill is CLEARED, not carried over - moving a
	 * study arm out from under a study part has to empty the part slot, or the
	 * key still claims to descend through a branch that is no longer above it.
	 *
	 * This is the shared primitive behind a QC Align re-parent, mirrored in
	 * `api/app/services/Dactylizer/CompositeKeyUtils.ts` (`relocateCompositeKey`)
	 * and in `api/app/utils/qc/branchRelocation.ts` (`relocateSegments`, the one
	 * the merge/dedupe/normalize seams actually run). All three must agree
	 * character for character, or Align, the Merge summary and Review render a
	 * relocated branch's key three different ways.
	 *
	 * @param {string} compositeKey - key to re-root
	 * @param {number} levelIndex - composite-key slot of the moved branch's level
	 * @param {string[]} ancestorSegments - destination chain, positional by slot
	 * @returns {string} the relocated composite key
	 *
	 * Example: an arm at slot 2 moved from "Study A || Part 1" to "Study B"
	 *   relocateCompositeKey("Study A || Part 1 || Arm A || ||", 2, ["Study B"])
	 *     → "Study B || || Arm A || ||"
	 */
	const relocateCompositeKey = (compositeKey, levelIndex, ancestorSegments) => {
		const parts = splitCompositeKey(compositeKey || "");
		for (let i = 0; i < levelIndex && i < parts.length; i++) {
			parts[i] = (ancestorSegments || [])[i] || "";
		}
		return joinCompositeKey(parts);
	};

	/**
	 * Re-key a composite key onto a branch it was PROJECTED into (DCTL-2626).
	 *
	 * The third and last of the branch-identity primitives. Rename changes a
	 * value in its own slot; relocation re-roots the slots ABOVE a moved branch;
	 * a projection is the reviewer asserting that one input's branch IS a branch
	 * of the other input's tree, at any level - so it overwrites every slot up to
	 * and including `max(sourceLevel, targetLevel)` and leaves what is deeper.
	 *
	 * **Why the max, and not the target's level alone.** The two agree whenever
	 * the target is deeper. They disagree on the case this exists for: a Subgroup
	 * projected onto a Population. Rewriting only to the target's level leaves
	 * the source's own Subgroup slot populated, so the key describes a subgroup
	 * UNDER the target instead of the target itself - a branch neither input has.
	 * A relocation keeps the moved branch's own slot because that branch still
	 * exists; a projection consumes it because the reviewer has said it is the
	 * target.
	 *
	 * Slots deeper than both levels survive, which is what keeps a projected
	 * branch's own descendants distinct rather than pooled into the target.
	 *
	 * Mirrors `projectSegments` in `api/app/utils/qc/branchRelocation.ts`, which
	 * is the one the merge, dedupe and normalize seams actually run. The two must
	 * agree character for character, or Align and the Merge summary render a
	 * projected branch's key two different ways.
	 *
	 * @param {string} compositeKey - key to re-key
	 * @param {number} sourceLevel - composite-key slot of the SOURCE branch's level
	 * @param {number} targetLevel - composite-key slot of the TARGET branch's level
	 * @param {string[]} targetSegments - the target's chain, positional by slot
	 * @returns {string} the projected composite key
	 *
	 * Example: a Subgroup (slot 4) projected onto a Population (slot 3)
	 *   projectCompositeKey("GAUSS || || rituximab || randomly assigned || FL",
	 *     4, 3, ["GAUSS", "Part 1", "Rituximab", "randomised"])
	 *     → "GAUSS || Part 1 || Rituximab || randomised ||"
	 */
	const projectCompositeKey = (
		compositeKey,
		sourceLevel,
		targetLevel,
		targetSegments,
	) => {
		const parts = splitCompositeKey(compositeKey || "");
		const upTo = Math.max(sourceLevel, targetLevel);
		for (let i = 0; i <= upTo && i < parts.length; i++) {
			parts[i] = (targetSegments || [])[i] || "";
		}
		return joinCompositeKey(parts);
	};

	/**
	 * Get the parent composite key for a given composite key
	 * Removes the rightmost non-empty value
	 *
	 * @param {string} compositeKey - Current composite key
	 * @returns {string} Parent composite key (empty string if already at root)
	 *
	 * Examples:
	 *   getParentKey("Study A || Part 1 || Arm A || ||")
	 *     → "Study A || Part 1 || || ||"
	 *
	 *   getParentKey("Study A || || || ||")
	 *     → ""
	 */
	const getParentKey = (compositeKey) => {
		if (!compositeKey) return "";

		const parts = splitCompositeKey(compositeKey);

		// Find the rightmost non-empty index
		let lastNonEmptyIndex = -1;
		for (let i = parts.length - 1; i >= 0; i--) {
			if (parts[i]) {
				lastNonEmptyIndex = i;
				break;
			}
		}

		// If no non-empty values, return empty string
		if (lastNonEmptyIndex === -1) {
			return "";
		}

		// Clear the last non-empty value
		parts[lastNonEmptyIndex] = "";

		// If all values are now empty, return empty string (this was a root node)
		if (parts.every((p) => !p)) {
			return "";
		}

		return joinCompositeKey(parts);
	};

	// True if key1 is a "child" of key2; e.g.
	// "study1 || part1 || " is a child of "study1 || || "
	// "study1 || part1 || arm1" is a child of "study1 || part1 || "
	// "study1 || part1 || " is NOT a child of "study1 || part1 || arm1"
	// "study1 || || arm1" is a child of "study1 || || "
	// "study1 || || arm1" is NOT a child of "study1 || part1 || arm1"
	// "study1 || part1 || arm1" is NOT a child of "study1 || || arm1"
	const isChildKey = (key1, key2) => {
		const child = parseCompositeKey(key1);
		const parent = parseCompositeKey(key2);

		// if all keys are empty in either child or parent, something has gone wrong
		// return false
		if (parsedKeys.map((key) => child[key]).every((value) => !value)) {
			return false;
		}

		if (parsedKeys.map((key) => parent[key]).every((value) => !value)) {
			return false;
		}

		let potentialChild = false;

		for (const key of parsedKeys) {
			if (!parent[key] && !child[key]) {
				continue;
			}

			// If key is present in both but the values different, then key1
			// is not a child
			if (parent[key] && child[key] && parent[key] !== child[key]) {
				return false;
			}

			// If key is present in key2 but not key1, then key1 is not a child
			if (parent[key] && !child[key]) {
				return false;
			}

			// If key is present in both and the values are the same, and previously
			// there was a potential child
			// (i.e. last key was not present in parent but was in child)
			if (potentialChild && parent[key] && child[key]) {
				return false;
			}

			if (!parent[key] && child[key]) {
				potentialChild = true;
			}
		}

		return true;
	};

	// Modifies a single value in a composite key, and returns the updated key
	const updateKey = (key, tag, value) => {
		if (!Object.keys(tagKeyMap).includes(tag))
			throw new Error(`Tag '${tag}' is not a valid part of a composite key`);

		const splitKey = splitCompositeKey(key);
		splitKey[tagKeyMap[tag]] = value;
		return joinCompositeKey(splitKey);
	};

	const getValueAtBranch = (key, tag) => {
		if (!Object.keys(tagKeyMap).includes(tag))
			throw new Error(`Tag '${tag}' is not a valid part of a composite key`);

		const splitKey = splitCompositeKey(key);
		return splitKey[tagKeyMap[tag]];
	};

	const isStudy = (key) => {
		const { study, studyPart, studyArm, population, subgroup } =
			parseCompositeKey(key);
		return !!study && !studyPart && !studyArm && !population && !subgroup;
	};

	const isStudyPart = (key) => {
		const { studyPart, studyArm, population, subgroup } =
			parseCompositeKey(key);
		return !!studyPart && !studyArm && !population && !subgroup;
	};

	const isStudyArm = (key) => {
		const { studyArm, population, subgroup } = parseCompositeKey(key);
		return !!studyArm && !population && !subgroup;
	};

	const isStudyPopulation = (key) => {
		const { population, subgroup } = parseCompositeKey(key);
		return !!population && !subgroup;
	};

	const isStudySubgroup = (key) => {
		const { subgroup } = parseCompositeKey(key);
		return !!subgroup;
	};

	const isEmpty = (key) => {
		const { study, studyPart, studyArm, population, subgroup } =
			parseCompositeKey(key);
		return !study && !studyPart && !studyArm && !population && !subgroup;
	};

	const isBranchTag = (tagName) => {
		return branchTags.includes(tagName);
	};
	// Strict canonical-name match. Use this from SDEv2 paths (under
	// src/pages/SourceDataExtraction), which only see Dactylizer-canonicalized
	// tag names. SDEv1 paths that read raw WPD save data should prefer
	// isReferenceBranchTag above, which also accepts the legacy "Reference
	// study arm abbreviation" name still baked into older saves.
	const isReferenceTag = (tagName) => {
		return (
			!!tagName &&
			(referenceTagNameSet.has(tagName) || tagName === referenceBranchTag)
		);
	};
	// Prefer this in remap/resolution paths: a reference data point's tagUuid is
	// stable across tag renames, unlike its tagName.
	const isReferenceTagByUuid = (tagUuid) => {
		return !!tagUuid && referenceTagUuidSet.has(tagUuid);
	};
	const isBranchTagGroup = (tagGroupName) => {
		return branchTagGroups.includes(tagGroupName);
	};

	const getTagGroupName = (key) => {
		const { study, studyPart, studyArm, population, subgroup } =
			parseCompositeKey(key);

		if (subgroup) {
			return subgroupTagGroup;
		} else if (population) {
			return populationTagGroup;
		} else if (studyArm) {
			return studyArmTagGroup;
		} else if (studyPart) {
			return studyPartTagGroup;
		} else if (study) {
			return studyTagGroup;
		}
	};

	// Returns valid child indices for a given parent level
	const getValidChildIndices = (parentIndex) => {
		const rules = {
			0: [1, 2],
			1: [2],
			2: [3, 4],
			3: [4],
			4: [],
		};
		return rules[parentIndex] || [];
	};

	// Helper to get index from type string
	const getIndexFromType = (type) => {
		return parsedKeys.indexOf(type);
	};

	// Helper to get type from index
	const getTypeFromIndex = (index) => {
		return parsedKeys[index];
	};

	// Helper to get label from type
	const getLabelFromType = (type) => {
		const index = getIndexFromType(type);
		return parsedLabels[index];
	};

	return {
		compositeKeyDelimiter,
		studyTag,
		studyPartTag,
		studyArmTag,
		populationTag,
		subgroupTag,
		referenceBranchTag,
		referenceBranchTagNames,
		isReferenceBranchTag,
		branchTags,
		branchTagGroups,
		tagKeyMap,
		studyKey,
		studyPartKey,
		studyArmKey,
		populationKey,
		subgroupKey,
		parsedKeys,
		parsedLabels,
		generateCompositeKey,
		splitCompositeKey,
		joinCompositeKey,
		buildCompositeKey,
		relocateCompositeKey,
		projectCompositeKey,
		getParentKey,
		parseCompositeKey,
		parseCompositeKeyWithLabels,
		isChildKey,
		updateKey,
		getValueAtBranch,
		isStudy,
		isStudyPart,
		isStudyArm,
		isStudyPopulation,
		isStudySubgroup,
		isEmpty,
		isBranchTag,
		isReferenceTag,
		isReferenceTagByUuid,
		isBranchTagGroup,
		getTagGroupName,
		getCompositeKeyValue,
		formatBranchPath,
		getValidChildIndices,
		getIndexFromType,
		getTypeFromIndex,
		getLabelFromType,
		getIconForLevel,
	};
};

export default createCompositeKeyUtils;
